/**
 * Deep-replaces NaN numbers with 0 before a form value is persisted.
 *
 * useCurrencyInput's handleChange forwards a synthetic event with
 * `valueAsNumber: Number.NaN` when a currency box is blanked (see its own
 * comment), and react-hook-form ends up holding that NaN in form state.
 * `JSON.stringify` silently turns `NaN` into `null` on save — which then
 * reads back indistinguishable from "this field was legitimately set to
 * null", corrupting anything downstream that treats "present but null" as
 * meaningful (see priorIncomeSeed.ts / CRITICAL 2, #1592 phase 2 review
 * round 2). Scrubbing NaN to 0 here, before the value ever reaches
 * JSON.stringify, keeps `null` out of storage in the first place.
 */
export function sanitizeNaN<T>(value: T): T {
  if (typeof value === 'number') {
    return (Number.isNaN(value) ? 0 : value) as T;
  }
  if (Array.isArray(value)) {
    const items = value as unknown[];
    return items.map(item => sanitizeNaN(item)) as T;
  }
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      out[key] = sanitizeNaN(source[key]);
    }
    return out as T;
  }
  return value;
}
